import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { hostAPI } from '../services/api';
import { ChevronLeft, Mail, Loader2, CheckCircle2 } from 'lucide-react';

const HostForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    try {
      setLoading(true);
      const res = await hostAPI.forgotPassword({ email });
      if (res.success) setSent(true);
    } catch (err) {
      // Backend always returns success to avoid enumeration, but keep a fallback
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-md">
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/host/login')}
            className="mb-4"
            data-testid="host-forgot-back-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to login
          </Button>
        </div>
        <Card className="shadow-xl" data-testid="host-forgot-card">
          <CardHeader className="text-center">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#7CB342] to-[#6da036] flex items-center justify-center mx-auto mb-4">
              {sent ? <CheckCircle2 className="h-8 w-8 text-white" /> : <Mail className="h-8 w-8 text-white" />}
            </div>
            <CardTitle className="text-2xl">{sent ? 'Check your email' : 'Forgot password?'}</CardTitle>
            <CardDescription>
              {sent
                ? "If an account exists with that email, we've sent a reset link. The link is valid for 1 hour."
                : "Enter your email and we'll send you a secure link to reset your password."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <Button
                onClick={() => navigate('/host/login')}
                className="w-full py-6 bg-[#7CB342] hover:bg-[#6da036] text-white"
                data-testid="host-forgot-back-to-login"
              >
                Back to Login
              </Button>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2"
                    data-testid="host-forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full py-6 bg-[#7CB342] hover:bg-[#6da036] text-white"
                  data-testid="host-forgot-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                  ) : 'Send Reset Link'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HostForgotPasswordPage;
